/**
 * Typography tokens.
 *
 * Scale: a *fluid* perfect-fourth (1.333) modular series at the desktop end,
 * minor-third (1.2) at the mobile end, locked together with `clamp()` between
 * a 360px and a 1280px viewport (atlas 03 §3, "Utopia"). The result is a
 * type system that breathes with the viewport instead of hard-stepping at
 * arbitrary breakpoints.
 *
 * Letter-spacing follows an *optical* curve: tighter (negative) at display
 * sizes where letters touch, neutral around body, slightly looser at caption
 * sizes where rendering thins (atlas 03 §1, Bringhurst rules). The per-size
 * `optical` map gives components the perceptually tuned value for a `scale`
 * size; the `caps` map gives the wider tracking for uppercase text.
 *
 * Line heights: tightened at display, generous at body. The `relaxed` token
 * sits at 1.65 (down from 1.75) — the previous value over-aerated body text
 * in dense compositions and broke the eye's saccadic rhythm.
 */

// Helper — produces a clamped fluid value between `minPx` (at 360px viewport)
// and `maxPx` (at 1280px viewport). The slope is computed in `vw` units so
// the browser linearly interpolates without media queries.
function fluid(minPx: number, maxPx: number): string {
    const minVw = 360;
    const maxVw = 1280;
    const slope = ((maxPx - minPx) * 100) / (maxVw - minVw);
    const intercept = minPx - (slope * minVw) / 100;
    const preferred = `calc(${intercept.toFixed(3)}px + ${slope.toFixed(3)}vw)`;
    return `clamp(${String(minPx)}px, ${preferred}, ${String(maxPx)}px)`;
}

export const typography = {
    family: {
        system: '"Roboto Flex", "Roboto Flex Fallback", Roboto, Arial, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        mono: '"JetBrains Mono", "JetBrains Mono Fallback", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        // `display` — Sage/Galileo branded voice for peak moments only. Bebas
        // is loaded optional, so fallback lands on the UI face without blocking.
        display:
            '"Bebas Neue", "Roboto Flex", "Roboto Flex Fallback", Roboto, Arial, ui-sans-serif, system-ui, sans-serif',
    },
    scale: {
        // 12px → 12.5px       — micro caption (badges, dense labels)
        xs: fluid(12, 12.5),
        // 13px → 14px         — secondary text, button labels
        sm: fluid(13, 14),
        // 14px → 15px         — list item titles, compact body
        base: fluid(14, 15),
        // 15px → 16.5px       — primary body, message bubbles
        md: fluid(15, 16.5),
        // 17px → 19px         — section headings within a panel
        lg: fluid(17, 19),
        // 20px → 24px         — composition titles
        xl: fluid(20, 24),
        // 26px → 32px         — view-level titles
        xxl: fluid(26, 32),
        // 34px → 44px         — hero / display
        xxxl: fluid(34, 44),
    },
    weights: {
        regular: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
    },
    lineHeights: {
        // Display sizes — leading approaches 1.0 to keep multi-line headings
        // from feeling vertically diluted.
        tight: 1.1,
        // Headings, button labels — compact but not cramped.
        snug: 1.3,
        // Default body.
        normal: 1.5,
        // Long-form prose. Reduced from 1.75 — that value was over-aerated
        // for the chat/list contexts where we actually use it.
        relaxed: 1.65,
    },
    /**
     * Per-size optical letter-spacing. Components rendering at a given
     * `scale` size can pull the matching value here without re-deriving.
     * The curve is monotonic: -0.022em at display, climbing to +0.020em at
     * micro-caption.
     */
    optical: {
        xs: "0.020em",
        sm: "0.010em",
        base: "0.005em",
        md: "0em",
        lg: "-0.008em",
        xl: "-0.014em",
        xxl: "-0.018em",
        xxxl: "-0.022em",
    },
    /**
     * Tracking for *all-caps* text. Uppercase letterforms have wider
     * sidebearings than lowercase, so they accumulate visual density
     * unless tracked open. Aesthetic-theory §3 (Bringhurst): "Letterspace
     * all strings of capitals." Three steps cover the practical surface:
     *
     *   - `loose`  (0.10em) — micro caps (eyebrows, very small labels).
     *                          The eye needs maximum air at 11–12px.
     *   - `medium` (0.06em) — UI labels, badge text. The default for
     *                          `caps` and `eyebrow` roles.
     *   - `tight`  (0.02em) — short ALL CAPS at heading sizes. Just
     *                          enough air to keep adjacent caps distinct
     *                          without diluting their weight.
     */
    caps: {
        loose: "0.10em",
        medium: "0.06em",
        tight: "0.02em",
    },
} as const;

// ---------------------------------------------------------------------------
// Typography roles — semantic type ramp (Aesthetic-theory §3 §7)
// ---------------------------------------------------------------------------
//
// Roles bundle the four perceptual concerns of a piece of running text —
// family, size, weight, leading, tracking, case — into one named token.
// Components and compositions reach for a *role* (semantic intent) instead
// of redefining the four values inline at every callsite. This is the
// single source of truth for the application's typographic voice.
//
// All-caps roles (`eyebrow`, `label`, `caps`) own the project-wide caps
// contract: uppercase + caps tracking + medium weight + low chroma tone.
// Use them anywhere caps are wanted; never write `textTransform: uppercase`
// inline.

export type TypographyRole =
    | "brand"
    | "display"
    | "title"
    | "heading"
    | "subheading"
    | "bodyLg"
    | "body"
    | "bodySm"
    | "caption"
    | "eyebrow"
    | "label"
    | "caps"
    | "button"
    | "mono";

export interface TypographyRoleSpec {
    /** Resolved scale key from `typography.scale`. */
    readonly size: TypographyScaleKey;
    /** Resolved weight key from `typography.weights`. */
    readonly weight: TypographyWeightKey;
    /** Resolved leading key from `typography.lineHeights`. */
    readonly leading: TypographyLineHeightKey;
    /**
     * Resolved tracking. References either an `optical` key (auto-tuned
     * by size) or a `caps` key (auto-tuned for uppercase). Components can
     * dereference with `typography.optical[k]` / `typography.caps[k]`.
     */
    readonly tracking:
        | { kind: "optical"; key: keyof typeof typography.optical }
        | { kind: "caps"; key: keyof typeof typography.caps };
    /** `none` (default) or `uppercase`. Italic/lowercase roles aren't part of the system. */
    readonly transform: "none" | "uppercase";
    /** Family override; `system` is the default and may be omitted. */
    readonly family?: keyof typeof typography.family;
    /** Tabular numerals — for roles that read alongside numeric data. */
    readonly tabular?: boolean;
}

export const typographyRoles: Record<TypographyRole, TypographyRoleSpec> = {
    brand: {
        size: "xl",
        weight: "regular",
        leading: "tight",
        tracking: { kind: "caps", key: "medium" },
        transform: "uppercase",
        family: "display",
    },
    display: {
        size: "xxxl",
        weight: "regular",
        leading: "tight",
        tracking: { kind: "caps", key: "tight" },
        transform: "uppercase",
        family: "display",
    },
    title: {
        size: "xxl",
        weight: "semibold",
        leading: "tight",
        tracking: { kind: "optical", key: "xxl" },
        transform: "none",
    },
    heading: {
        size: "xl",
        weight: "semibold",
        leading: "snug",
        tracking: { kind: "optical", key: "xl" },
        transform: "none",
    },
    subheading: {
        size: "lg",
        weight: "semibold",
        leading: "snug",
        tracking: { kind: "optical", key: "lg" },
        transform: "none",
    },
    bodyLg: {
        size: "md",
        weight: "regular",
        leading: "relaxed",
        tracking: { kind: "optical", key: "md" },
        transform: "none",
    },
    body: {
        size: "base",
        weight: "regular",
        leading: "normal",
        tracking: { kind: "optical", key: "base" },
        transform: "none",
    },
    bodySm: {
        size: "sm",
        weight: "regular",
        leading: "snug",
        tracking: { kind: "optical", key: "sm" },
        transform: "none",
    },
    caption: {
        size: "xs",
        weight: "regular",
        leading: "snug",
        tracking: { kind: "optical", key: "xs" },
        transform: "none",
    },
    // ── all-caps contract ───────────────────────────────────────────────
    eyebrow: {
        // The "kicker" above a heading. Smaller than label, looser tracked
        // because it sits on its own line with maximum surrounding air.
        size: "xs",
        weight: "medium",
        leading: "snug",
        tracking: { kind: "caps", key: "loose" },
        transform: "uppercase",
    },
    label: {
        // Form labels, group labels, segmented headers. Larger and tighter
        // than eyebrow because it usually sits in a denser composition.
        size: "xs",
        weight: "semibold",
        leading: "snug",
        tracking: { kind: "caps", key: "medium" },
        transform: "uppercase",
    },
    caps: {
        // Generic UPPERCASE label for chips, status pills, navigation eyebrows.
        // Sits one notch above `label` in size for chip-readable presence.
        size: "sm",
        weight: "semibold",
        leading: "snug",
        tracking: { kind: "caps", key: "medium" },
        transform: "uppercase",
    },
    button: {
        // Button label voice — semibold, mid-tracked, sentence-case.
        size: "sm",
        weight: "semibold",
        leading: "snug",
        tracking: { kind: "optical", key: "sm" },
        transform: "none",
    },
    mono: {
        // Monospace — for entity ids, codes, technical metadata.
        size: "sm",
        weight: "regular",
        leading: "snug",
        tracking: { kind: "optical", key: "sm" },
        transform: "none",
        family: "mono",
        tabular: true,
    },
};

export type TypographyTokens = typeof typography;
export type TypographyScaleKey = keyof typeof typography.scale;
export type TypographyWeightKey = keyof typeof typography.weights;
export type TypographyLineHeightKey = keyof typeof typography.lineHeights;
export type TypographyOpticalKey = keyof typeof typography.optical;
export type TypographyCapsKey = keyof typeof typography.caps;
export type TypographyRoleKey = TypographyRole;

/**
 * Resolve a role spec to a concrete bundle of CSS values. Components that
 * need to apply a role inline can call this once and spread the result
 * into their style object — no repeated lookups, no drift between
 * callsites.
 */
export function resolveTypographyRole(role: TypographyRoleKey): {
    readonly fontFamily: string;
    readonly fontSize: string;
    readonly fontWeight: number;
    readonly lineHeight: number;
    readonly letterSpacing: string;
    readonly textTransform: "none" | "uppercase";
    readonly fontVariantNumeric: string | undefined;
} {
    const spec = typographyRoles[role];
    const family = typography.family[spec.family ?? "system"];
    const tracking =
        spec.tracking.kind === "caps"
            ? typography.caps[spec.tracking.key]
            : typography.optical[spec.tracking.key];
    return {
        fontFamily: family,
        fontSize: typography.scale[spec.size],
        fontWeight: typography.weights[spec.weight],
        lineHeight: typography.lineHeights[spec.leading],
        letterSpacing: tracking,
        textTransform: spec.transform,
        fontVariantNumeric: spec.tabular === true ? "tabular-nums" : undefined,
    };
}
